import React from "react";

import Box, { BoxProps } from "@mui/material/Box";
import Button from "@mui/material/Button";
import { styled } from "@mui/material/styles";
import Tooltip, { TooltipProps, tooltipClasses } from "@mui/material/Tooltip";

import { TAG_CLASSES } from "~/components/tag/constants";
import { ChipType } from "~/theme/chip";
import {
  COLOR_ADDITIONAL_WARM_GRAY_3U,
  COLOR_BLACK,
  COLOR_GREYSCALE_BLACK_54,
  COLOR_GREYSCALE_BLACK_75,
  COLOR_NEON_GREEN,
  COLOR_PRIMARY_WHITE,
  COLOR_WHITE,
  COLOR_ERROR,
} from "~/theme/colors";
import { getFormatMedia } from "~/theme/utils";

import {
  FILTERS_CHECKBOX_SHARED_CLASSES,
  SHOP_FILTER_CLASSES,
} from "./filter-shared.constants";

interface StylesProps {
  isShopFilter?: boolean;
  isCurrentStore?: boolean;
  showNewIcon?: boolean;
}

const filterItemSharedValues = {
  position: "relative" as const,
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-start",
  width: "auto",
  boxSizing: "border-box" as const,
  minHeight: 48,
};
const filterCheckBoxSharedValues = {
  [`& .${FILTERS_CHECKBOX_SHARED_CLASSES.formControlRoot}`]: {
    width: "100%",
  },
  [`& .${FILTERS_CHECKBOX_SHARED_CLASSES.formControlLabel}`]: {
    width: "100%",
  },
  [`& .${FILTERS_CHECKBOX_SHARED_CLASSES.checkBox}`]: {
    margin: 0,
  },
  [`& .${FILTERS_CHECKBOX_SHARED_CLASSES.filterItemLabel}`]: {
    width: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    paddingLeft: 10,
  },
};

export const ColorFilterContainer = styled(Box)(({ theme }) => {
  const formatMedia = getFormatMedia(theme);
  const { BREAKPOINT_DESKTOP } = formatMedia;

  return {
    ...filterItemSharedValues,
    ...filterCheckBoxSharedValues,
    padding: theme.spacing(0, 2),
    "& > label": {
      margin: 0,
      textTransform: "capitalize",
    },
    [BREAKPOINT_DESKTOP]: {
      minHeight: 40,
    },
    [`& .${FILTERS_CHECKBOX_SHARED_CLASSES.colorFilterCheckbox}`]: {
      minWidth: 32,
      minHeight: 32,
      margin: 0,
      position: "relative",
      backgroundSize: "cover",
      borderRadius: "0",
      "&:hover": {
        filter: "brightness(85%)",
      },
      "& > svg": {
        "& > *": {
          "&:first-of-type": {
            fill: "none",
            strokeWidth: 0,
          },
        },
      },
    },
    [`& .${FILTERS_CHECKBOX_SHARED_CLASSES.colorFilterCheckboxChecked}`]: {
      "&  > svg > *": {
        "&:first-of-type": {
          fill: "none",
          strokeWidth: 3,
        },
        "&:nth-of-type(2)": {
          strokeWidth: 2,
        },
      },
    },
    [`& .${FILTERS_CHECKBOX_SHARED_CLASSES.whiteCheckbox}`]: {
      "&  > svg > *:nth-of-type(2)": {
        stroke: COLOR_PRIMARY_WHITE,
      },
    },
  };
});

export const MultiFilterContainer = styled(Box)(({ theme }) => {
  const formatMedia = getFormatMedia(theme);
  const { BREAKPOINT_DESKTOP } = formatMedia;

  return {
    ...filterItemSharedValues,
    ...filterCheckBoxSharedValues,
    padding: theme.spacing(1.25, 2, 1.25, 2.25),
    "& > label": {
      margin: 0,
      textTransform: "capitalize",
      alignItems: "flex-start",
    },
    [BREAKPOINT_DESKTOP]: {
      minHeight: 40,
    },
  };
});
export const SwatchFilterButtonContainer = styled(Button)(({ theme }) => {
  const formatMedia = getFormatMedia(theme);
  const { BREAKPOINT_DESKTOP } = formatMedia;

  return {
    ...filterItemSharedValues,
    width: "100%",
    padding: theme.spacing(1.75, 2),
    "&:focus:before": {
      boxSizing: "border-box",
      top: 0,
      bottom: 0,
      left: 0,
      right: 0,
    },
    [BREAKPOINT_DESKTOP]: {
      minHeight: 40,
    },
    [`& .${FILTERS_CHECKBOX_SHARED_CLASSES.selectedCategory}`]: {
      marginRight: theme.spacing(2),
      display: "flex",
      alignItems: "center",
    },
    [`& .${FILTERS_CHECKBOX_SHARED_CLASSES.numberOfItems}`]: {
      position: "absolute",
      right: theme.spacing(2),
    },
  };
});
export const ShopFilterContainer = styled(
  ({
    isCurrentStore,
    isShopFilter,
    showNewIcon,
    ...restProps
  }: StylesProps & BoxProps) => <Box {...restProps} />,
)(({ theme, isCurrentStore, isShopFilter, showNewIcon }) => {
  const formatMedia = getFormatMedia(theme);
  const { BREAKPOINT_DESKTOP } = formatMedia;

  return {
    backgroundColor: COLOR_ADDITIONAL_WARM_GRAY_3U,
    padding: theme.spacing(1.75, 0, 1.75, 4),
    [BREAKPOINT_DESKTOP]: {
      padding: theme.spacing(1.75, 0, 1.75, 1.5),
    },

    [`& .${SHOP_FILTER_CLASSES.filterItemShop}`]: {
      ...filterItemSharedValues,
      flex: "auto",
      alignItems: isShopFilter && isCurrentStore ? "flex-start" : "center",
      minHeight: "auto",

      "& > label": {
        width: "100%",
        margin: 0,
        alignItems: isShopFilter && isCurrentStore ? "flex-start" : "center",
      },
      [BREAKPOINT_DESKTOP]: {
        minHeight: "40px",
      },
    },
    [`& .${SHOP_FILTER_CLASSES.shopFilterCheckbox}`]: {
      marginRight: theme.spacing(2.25),
      alignSelf: "flex-start",
      marginLeft: theme.spacing(0),
      [BREAKPOINT_DESKTOP]: {
        marginRight: theme.spacing(1.25),
        marginLeft: theme.spacing(1),
      },
    },
    [`& .${SHOP_FILTER_CLASSES.editButton}`]: {
      position: "absolute",
      right: theme.spacing(2),
      top: theme.spacing(1),
      marginLeft: "auto !important",
    },
    [`& .${SHOP_FILTER_CLASSES.storeAddressCity}`]: {
      fontWeight: 700,
      marginTop: theme.spacing(1),
      [BREAKPOINT_DESKTOP]: {
        maxWidth: "150px",
      },
    },
    [`& .${SHOP_FILTER_CLASSES.storeAddress}`]: {
      [BREAKPOINT_DESKTOP]: {
        maxWidth: "191px",
      },
    },
    [`& .${SHOP_FILTER_CLASSES.productFilter}`]: {
      display: "flex",
      flexDirection: "column",
      padding: theme.spacing(1.5, 0, 2.5, 0),
    },
    [`& .${SHOP_FILTER_CLASSES.productFilterTag}`]: {
      marginBottom: theme.spacing(1.5),
      width: 159,
      height: "20px !important",
      fontSize: "11px !important",

      [`&.${TAG_CLASSES.chipTag}`]: {
        [`&.${ChipType.tag}`]: {
          backgroundColor: COLOR_NEON_GREEN,
          color: COLOR_BLACK,
          "&:hover": {
            color: COLOR_GREYSCALE_BLACK_75,
          },
        },
      },
    },
    [`& .${SHOP_FILTER_CLASSES.productFilterSelect}`]: {
      width: 303,
      minHeight: 48,
      backgroundColor: COLOR_WHITE,
      "& .MuiSelect-select:focus": {
        backgroundColor: COLOR_WHITE,
      },
      [formatMedia.BREAKPOINT_TABLET]: {
        width: 327,
      },
      [formatMedia.BREAKPOINT_DESKTOP]: {
        width: 191,
      },
    },
    [`& .${SHOP_FILTER_CLASSES.productWithoutClickAndCollect}`]: {
      display: "flex",
      alignItems: "center",
      padding: theme.spacing(1.9, 0, 2.5, 0),
      [formatMedia.BREAKPOINT_DESKTOP]: {
        maxWidth: 191,
        margin: theme.spacing(0, "auto"),
        alignItems: "start",
      },
      "& > svg": {
        color: showNewIcon ? COLOR_ERROR : COLOR_GREYSCALE_BLACK_54,
      },
    },
    [`& .${SHOP_FILTER_CLASSES.productWithoutClickAndCollectText}`]: {
      marginLeft: theme.spacing(1.4),
      whiteSpace: "normal",
    },
  };
});

export const ShopFilterTooltipStyled = styled(
  ({ className, children, ...props }: TooltipProps) => (
    <Tooltip {...props} classes={{ popper: className }}>
      {children}
    </Tooltip>
  ),
)(({ theme }) => {
  const formatMedia = getFormatMedia(theme);
  const { BREAKPOINT_DESKTOP, BREAKPOINT_TABLET } = formatMedia;
  return {
    [`& .${tooltipClasses.tooltipArrow}`]: {
      maxWidth: "273px",
      padding: theme.spacing(2),
      ...theme.typography.body2,
      background: COLOR_WHITE,
      borderRadius: 0,
      color: COLOR_BLACK,
      boxShadow: `0px 4px 16px 0px rgba(0, 0, 0, 0.15)`,
      [`&.${SHOP_FILTER_CLASSES.tooltipNotCC}`]: {
        right: theme.spacing(1),
        top: theme.spacing(4.5),
      },
      [`&.${SHOP_FILTER_CLASSES.tooltipIsCC}`]: {
        top: theme.spacing(1.25),
      },
    },
    [`& .${tooltipClasses.arrow}`]: {
      left: "-47% !important",
      [BREAKPOINT_TABLET]: {
        left: "-50% !important",
      },
      [BREAKPOINT_DESKTOP]: {
        left: "-27% !important",
      },
      "&::before": {
        background: COLOR_WHITE,
      },
    },
  };
});
